import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dropdown, Bubble, Input, Button, Select } from 'tea-component';
import { LocaleType } from '@/pages/opinion/types/common';
import type { PlaceholderType } from '../../types';
import { NO_DATE } from '@op/constants';
import {
  compareOptionList,
  compareStepMap,
} from '@op/constants/specialOptions';
import '../scss/range.scss';
import './index.scss';
import { getStartAndEndValue } from '../../utils';
import { FILTER_CONST } from '../../constants';
import useLanguage from '@op/hooks/useLanguage';
import { addFilterBuriedPoint } from '../../utils/point';
import { AllFilterPointType } from '@op/operationLog/pages/filter';

const regexText = /^\d+(\.\d{1,3})?$/;
const empty = 'Please enter numbers, symbols are not supported.';
const valueError = 'Please enter a numerical value to three decimal places.';
function CustomThanRange(
  {
    lang,
    rangeValue,
    setRangeValue,
    placeholder,
    unit = 'K',
    verifyFunc,
    pointKey,
  }: {
    lang: LocaleType;
    rangeValue: { sign: string; start: string; end: string };
    setRangeValue: (value: {
      sign: string | number;
      start: string | number;
      end: string | number;
    }) => void;
    placeholder: PlaceholderType;
    unit?: string;
    verifyFunc?: (value: { start: string; end: string; sign: string }) => {
      startErrorText: string;
      endErrorText: string;
      flag: boolean;
    };
    pointKey?: AllFilterPointType;
  },
  ref: React.Ref<unknown> | undefined,
) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [inputStartErrorText, setInputStartErrorText] = useState<string | null>(
    null,
  );
  const [inputEndErrorText, setInputEndErrorText] = useState<string | null>(
    null,
  );
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  const [sign, setSign] = useState('>=');

  function restDefaultRange() {
    setStartRange(rangeValue.start);
    setEndRange(rangeValue.end);
    setSign(rangeValue.sign || FILTER_CONST.THAN_RANGE_CUSTOMIZE);
  }

  useEffect(() => {
    restDefaultRange();
  }, [rangeValue]);

  function verifyNoIntervalValue(isSimple: boolean) {
    let endErrorText = '';
    if (endRange !== '' && !regexText.test(endRange)) {
      endErrorText = valueError;
    }

    if (!isSimple) {
      const end = Number(endRange);
      if (end < 0 || isNaN(end)) {
        endErrorText = empty;
      }
    }

    if (verifyFunc) {
      const { flag, endErrorText: localEndErrorText } = verifyFunc({
        start: startRange,
        end: endRange,
        sign,
      });

      if (flag) {
        endErrorText = localEndErrorText;
      }
    }

    return {
      startErrorText: null,
      endErrorText,
      isFalse: !!endErrorText,
    };
  }

  function verifyIntervalValue(isSimple: boolean) {
    let startErrorText = '';
    let endErrorText = '';
    if (startRange !== '' && !regexText.test(startRange)) {
      startErrorText = valueError;
    }

    if (endRange !== '' && !regexText.test(endRange)) {
      endErrorText = valueError;
    }

    if (!isSimple) {
      const start = Number(startRange);
      const end = Number(endRange);

      if (start < 0 || isNaN(start) || end < 0 || isNaN(end)) {
        startErrorText = empty;
        endErrorText = empty;
      }
    }

    if (verifyFunc) {
      const {
        flag,
        endErrorText: localEndErrorText,
        startErrorText: localStartErrorText,
      } = verifyFunc({
        start: startRange,
        end: endRange,
        sign,
      });

      if (flag) {
        endErrorText = localEndErrorText;
        startErrorText = localStartErrorText;
      }
    }

    return {
      startErrorText,
      endErrorText,
      isFalse: !!startErrorText || !!endErrorText,
    };
  }

  function getErrorMessage(isSimple = true) {
    const verifyFunc =
      sign === FILTER_CONST.THAN_RANGE_CUSTOMIZE
        ? verifyIntervalValue
        : verifyNoIntervalValue;
    return verifyFunc(isSimple);
  }

  function changeInputState(isSimple = true) {
    const { startErrorText, endErrorText, isFalse } = getErrorMessage(isSimple);

    setInputStartErrorText(startErrorText);
    setInputEndErrorText(endErrorText);
    return isFalse;
  }

  useEffect(() => {
    changeInputState();
  }, [startRange, endRange, sign]);

  const btnDisabled = useMemo(() => {
    const { isFalse } = getErrorMessage(false);

    return isFalse;
  }, [startRange, endRange, sign]);

  const noUpdateRef = useRef(false);
  const checkInput = (close: any) => {
    if (changeInputState(false)) return;

    const [start, end] = getStartAndEndValue(startRange, endRange);

    const newRange = {
      start,
      end,
      sign: sign === FILTER_CONST.THAN_RANGE_CUSTOMIZE ? '' : sign,
    };

    setRangeValue(newRange);
    noUpdateRef.current = true;
    close();

    setInputStartErrorText(null);
    setInputEndErrorText(null);
  };

  const getRangeButton = ({
    start,
    end,
    sign,
  }: {
    start: string;
    end: string;
    sign: string;
  }) => {
    const realSign = compareStepMap[sign];

    const [lStart, lEnd] = getStartAndEndValue(start, end);

    return sign === FILTER_CONST.THAN_RANGE_CUSTOMIZE
      ? `${lStart}% ~ ${lEnd}%`
      : `${realSign} ${end}%`;
  };

  useImperativeHandle(ref, () => ({
    buttonElement: getRangeButton,
  }));

  return (
    <div className="custom-than-range-wrapper">
      <Dropdown
        className="dbo-followerfilter"
        button={
          <div className="dbo-followerfilter-inner">
            <span className="select-button-text">{placeholder[lang]}</span>
            <span className="dbo-followerfilter-value">
              {getRangeButton({ start: startRange, end: endRange, sign })}
            </span>
          </div>
        }
        size="m"
        appearance="button"
        clickClose={false}
        onClose={() => {
          !noUpdateRef.current && restDefaultRange();
          noUpdateRef.current = false;
          try {
            addFilterBuriedPoint(
              pointKey,
              getRangeButton({ start: startRange, end: endRange, sign }),
            );
          } catch (error) {}
        }}
      >
        {(close) => (
          <div className="dbo-custom-than-wrap dbo-followerfilter-input-wrap">
            <div className="dbo-custom-than-select dbo-follower-filter-select">
              <Select
                size="m"
                appearance="button"
                matchButtonWidth
                value={sign}
                onChange={(value) => setSign(value)}
                options={formatOptionsMessage([
                  ...compareOptionList,
                  {
                    text: 'Interval',
                    value: FILTER_CONST.THAN_RANGE_CUSTOMIZE,
                  },
                ])}
              />
            </div>
            {sign === FILTER_CONST.THAN_RANGE_CUSTOMIZE ? (
              <div className="dbo-custom-than-input dbo-followerfilter-input">
                <div
                  className={`dbo-custom-than-input-item dbo-followerfilter-input-item ${
                    inputStartErrorText ? 'error' : ''
                  }`}
                >
                  <Bubble
                    error
                    content={formatMessage({
                      id: inputStartErrorText || NO_DATE.EMPTY,
                    })}
                    visible={!!inputStartErrorText}
                  >
                    <Input
                      value={startRange}
                      onChange={(value) => setStartRange(value)}
                      placeholder=""
                    />
                  </Bubble>
                  <div className="dbo-followerfilter-input-unit">{unit}</div>
                </div>

                <span className="dbo-followerfilter-input-gap">-</span>
                <div
                  className={`dbo-followerfilter-input-item ${
                    inputEndErrorText ? 'error' : ''
                  }`}
                >
                  <Bubble
                    error
                    content={formatMessage({
                      id: inputEndErrorText || NO_DATE.EMPTY,
                    })}
                    visible={!!inputEndErrorText}
                  >
                    <Input
                      value={endRange}
                      onChange={(value) => setEndRange(value)}
                      placeholder=""
                    />
                  </Bubble>
                  <div className="dbo-followerfilter-input-unit">{unit}</div>
                </div>
              </div>
            ) : (
              <div
                className={`dbo-custom-than-input-item dbo-followerfilter-input-item all ${
                  inputEndErrorText ? 'error' : ''
                }`}
              >
                <Bubble
                  error
                  content={formatMessage({
                    id: inputEndErrorText || NO_DATE.EMPTY,
                  })}
                  visible={!!inputEndErrorText}
                >
                  <Input
                    value={endRange}
                    onChange={(value) => setEndRange(value)}
                    placeholder=""
                  />
                </Bubble>
                <div className="dbo-followerfilter-input-unit">{unit}</div>
              </div>
            )}
            <div className="dbo-followerfilter-btngroup">
              <Button
                disabled={btnDisabled}
                type="primary"
                onClick={() => checkInput(close)}
              >
                {formatMessage({ id: 'Confirm' })}
              </Button>
              <Button
                type="weak"
                onClick={() => {
                  close();
                }}
              >
                {formatMessage({ id: 'Cancel' })}
              </Button>
            </div>
          </div>
        )}
      </Dropdown>
    </div>
  );
}

export default forwardRef(CustomThanRange);
